import React, { useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { UseFormSetValue } from 'react-hook-form';
import { FlavourFormData } from '../../types';

interface ImageUploaderProps {
  label: string;
  fieldName: keyof FlavourFormData;
  preview: string | null;
  onPreviewChange: (preview: string | null) => void;
  setValue: UseFormSetValue<FlavourFormData>;
  inputId: string;
  isRequired?: boolean;
  error?: string;
  description?: string;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  label,
  fieldName,
  preview,
  onPreviewChange,
  setValue,
  inputId,
  isRequired = false,
  error,
  description,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      setValue(fieldName, file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      onPreviewChange(previewUrl);
    }
  };

  const handleRemoveFile = () => {
    setValue(fieldName, undefined as any);
    onPreviewChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {isRequired && <span className="text-red-500">*</span>}
      </label>
      
      {description && (
        <p className="text-xs text-gray-500 mb-2">{description}</p>
      )}

      <div className="space-y-2">
        {/* File Input */}
        <input
          ref={fileInputRef}
          type="file"
          id={inputId}
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Upload Area */}
        <div
          onClick={handleClick}
          className={`
            relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${preview 
              ? 'border-green-300 bg-green-50' 
              : 'border-gray-300 hover:border-gray-400'
            }
            ${error ? 'border-red-300' : ''}
          `}
        >
          {preview ? (
            <div className="space-y-2">
              <img
                src={preview}
                alt="Preview"
                className="mx-auto h-24 w-24 object-cover rounded-lg"
              />
              <p className="text-sm text-green-600 font-medium">Image selected</p>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile();
                }}
                className="inline-flex items-center px-2 py-1 text-xs text-red-600 hover:text-red-800"
              >
                <X className="h-3 w-3 mr-1" />
                Remove
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">
                  <span className="font-medium text-primary">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
              </div>
            </div>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
      </div>
    </div>
  );
};

export default ImageUploader;
